import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, Sparkles, Zap, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/services/apiClient';

const RAZORPAY_KEY = import.meta.env.VITE_RAZORPAY_KEY_ID || '';

const PLANS = [
  {
    id: 'free',
    name: 'FREE',
    price: 0,
    period: 'for 7 days',
    description: 'Perfect for trying out the platform',
    features: [
      '1 Compset',
      'Up to 5 Competitor Tracking',
      '1 Real-Time Refresh / Day',
      'Competitive Pricing Intelligence',
      '30-Days Out Pricing Window',
    ],
    icon: Sparkles,
    popular: false,
    buttonText: 'Start Free Trial',
  },
  {
    id: 'tier1',
    name: 'TIER 1',
    price: 1499,
    period: 'per month',
    description: 'For small to medium hotels',
    features: [
      '1 Compset',
      'Up to 5 Competitors per Compset',
      '3 Real-Time Refreshes / Day',
      'Pricing Intelligence (competitor rates, price gaps, weekend trends)',
      'Basic Review Intelligence (rating trends, review volume only)',
      '30-Day Forecast Window',
      'Email Support',
    ],
    icon: Zap,
    popular: true,
    buttonText: 'Choose Tier 1',
  },
  {
    id: 'tier2',
    name: 'TIER 2',
    price: 2999,
    period: 'per month',
    description: 'For hotel chains and power users',
    features: [
      '3 Compsets',
      'Up to 5 Competitors per Compset',
      '5 Real-Time Refreshes / Day',
      'Pricing + Review Intelligence',
      'Rate Parity Monitoring',
      'Custom Scheduled Refreshes',
      'Priority Support',
    ],
    icon: Crown,
    popular: false,
    buttonText: 'Choose Tier 2',
  },
];

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PlanSelection = ({ onSelectPlan, currentPlan = null }) => {
  const [selectedPlan, setSelectedPlan] = useState(currentPlan || 'free');
  const [loading, setLoading] = useState(false);

  const handleSelectPlan = async (planId) => {
    setSelectedPlan(planId);
  };

  const handleContinue = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    const plan = PLANS.find(p => p.id === selectedPlan);
    
    // Handle free plan
    if (plan.price === 0) {
      toast.success('Free trial activated!');
      if (onSelectPlan) {
        onSelectPlan(selectedPlan);
      }
      return;
    }

    setLoading(true);
    
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Create order on backend
      const orderResponse = await apiClient.post('/api/payments/create-order/', {
        plan_id: selectedPlan
      });
      
      const order = orderResponse.data;
      
      // If mock order (Razorpay not configured), simulate success
      if (order.mock) {
        toast.info('Payment gateway not configured. Simulating payment...', { duration: 2000 });
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Verify mock payment
        await apiClient.post('/api/payments/verify/', {
          razorpay_order_id: order.id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
          plan_id: selectedPlan
        });
        
        toast.success(`${plan.name} plan activated!`);
        if (onSelectPlan) {
          onSelectPlan(selectedPlan);
        }
        return;
      }

      // Configure Razorpay options
      const options = {
        key: RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'Prowen',
        description: `${plan.name} Subscription`,
        order_id: order.id,
        handler: async function (response) {
          try {
            // Verify payment on backend
            await apiClient.post('/api/payments/verify/', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selectedPlan
            });
            
            toast.success(`${plan.name} plan activated!`);
            if (onSelectPlan) {
              onSelectPlan(selectedPlan);
            }
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: '',
          email: '',
          contact: ''
        },
        theme: {
          color: '#6366f1'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            toast.info('Payment cancelled');
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      toast.error('Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="plan-selection-page">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-2">Plan Selection</h1>
        <p className="text-muted-foreground">
          Choose the plan that best fits your hotel's needs. You can always upgrade or adjust later as your business grows.
        </p>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          const isSelected = selectedPlan === plan.id;
          
          return (
            <Card 
              key={plan.id}
              className={`relative cursor-pointer transition-all duration-200 hover:shadow-lg ${
                isSelected 
                  ? 'border-2 border-primary ring-2 ring-primary/20' 
                  : 'border hover:border-primary/50'
              }`}
              onClick={() => handleSelectPlan(plan.id)}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                  Most Popular
                </Badge>
              )}
              
              {/* Selection Indicator */}
              <div className="absolute top-4 right-4">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  isSelected 
                    ? 'border-primary bg-primary' 
                    : 'border-muted-foreground/30'
                }`}>
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>

              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-2 p-3 rounded-full bg-primary/10 w-fit">
                  <Icon className={`w-6 h-6 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <CardTitle className="text-lg">{plan?.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-3xl font-bold">₹{plan?.price.toLocaleString()}</span>
                  <span className="text-muted-foreground text-sm ml-1">/ {plan?.period}</span>
                </div>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3">
                  {plan?.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Continue Button */}
      <div className="flex justify-center">
        <Button 
          size="lg" 
          onClick={handleContinue}
          disabled={loading}
          className="min-w-[200px]"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>

      {/* Terms Note */}
      <p className="text-center text-xs text-muted-foreground max-w-md mx-auto">
        By selecting a plan, you agree to our Terms of Service and Privacy Policy. 
        Paid plans will be charged monthly. Cancel anytime.
      </p>
    </div>
  );
};

export default PlanSelection;
